import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
const { FiX, FiBold, FiItalic, FiList, FiLock, FiAlertTriangle } = FiIcons;
import SafeIcon from '../common/SafeIcon';
import { sanitizeHtml, escapeToHtml, htmlToPlainText, CLOUD_SYNC_WARNING_CHARS, CLOUD_SYNC_DANGER_CHARS, CLOUD_SYNC_SAFE_LIMIT, GOOGLE_SHEETS_CELL_LIMIT } from '../utils/constants';

export default function SnippetModal({ onClose, onSave, editingSnippet = null }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState('plain');
  const [isPermanent, setIsPermanent] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    if (editingSnippet) {
      setTitle(editingSnippet.title);
      setContent(editingSnippet.content);
      setType(editingSnippet.type);
      setIsPermanent(Boolean(editingSnippet.isPermanent));
    }
  }, [editingSnippet]);

  useEffect(() => {
    if (type === 'html' && editorRef.current && editorRef.current.innerHTML !== content) {
      editorRef.current.innerHTML = content || '';
    }
  }, [type, content]);

  const charCount = typeof content === "string" ? content.length : 0;
  const cloudContent = type === 'html' ? sanitizeHtml(content) : content;
  const cloudCharCount = typeof cloudContent === 'string' ? cloudContent.length : 0;
  const overCloudLimit = cloudCharCount > CLOUD_SYNC_SAFE_LIMIT;
  const nearCloudLimit = cloudCharCount >= CLOUD_SYNC_DANGER_CHARS;
  const warningCloudLimit = cloudCharCount >= CLOUD_SYNC_WARNING_CHARS;
  const charsRemaining = Math.max(0, CLOUD_SYNC_SAFE_LIMIT - cloudCharCount);

  const handleInput = () => {
    if (type === 'html' && editorRef.current) setContent(editorRef.current.innerHTML);
  };

  const execCommand = (cmd) => {
    document.execCommand(cmd, false, null);
    editorRef.current?.focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    if (overCloudLimit) return;
    onSave({
      title,
      content: type === 'html' ? sanitizeHtml(content) : content,
      type,
      isPermanent
    });
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-md">
      <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }} className="bg-gray-900 border-t sm:border border-gray-700 rounded-t-3xl sm:rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[85vh] overflow-hidden">
        <div className="p-5 border-b border-gray-800 flex justify-between items-center bg-gray-900/50 sticky top-0 z-10">
          <h2 className="text-lg font-bold text-white">{editingSnippet ? 'Edit Snippet' : 'New Snippet'}</h2>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-800 rounded-full transition-all"><SafeIcon icon={FiX} className="text-xl" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-1.5 ml-1">Title</label>
              <input autoFocus type="text" className="w-full bg-gray-950 border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all" value={title} onChange={(e) => setTitle(e.target.value)} required />
            </div>
            <div className="flex items-center gap-3 p-4 bg-indigo-500/5 border border-indigo-500/10 rounded-2xl">
              <input id="permanent" type="checkbox" className="w-5 h-5 text-indigo-600 bg-gray-700 border-gray-600 rounded-lg focus:ring-indigo-500" checked={isPermanent} onChange={(e) => setIsPermanent(e.target.checked)} />
              <label htmlFor="permanent" className="flex flex-col cursor-pointer select-none">
                <span className="text-sm font-bold text-gray-200 flex items-center gap-2"><SafeIcon icon={FiLock} className="text-indigo-400" /> Permanent Vault Entry</span>
                <span className="text-[10px] text-gray-500 font-medium">Never purged by retention and copying does not reset its retention clock.</span>
              </label>
            </div>
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-black text-gray-500 uppercase tracking-widest ml-1">Content</label>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${overCloudLimit ? 'bg-red-500/20 text-red-400' : nearCloudLimit ? 'bg-orange-500/20 text-orange-400' : warningCloudLimit ? 'bg-amber-500/20 text-amber-400' : 'bg-gray-700 text-gray-200'}`}>
                  {cloudCharCount.toLocaleString()} / {CLOUD_SYNC_SAFE_LIMIT.toLocaleString()} chars
                </span>
              </div>
              {type === 'html' && (
                <div className="flex gap-1 bg-gray-950/50 rounded-lg p-1 border border-gray-800 mb-2">
                  <button type="button" onClick={() => execCommand('bold')} className="p-1.5 hover:bg-gray-700 rounded text-gray-400"><SafeIcon icon={FiBold} /></button>
                  <button type="button" onClick={() => execCommand('italic')} className="p-1.5 hover:bg-gray-700 rounded text-gray-400"><SafeIcon icon={FiItalic} /></button>
                  <button type="button" onClick={() => execCommand('insertUnorderedList')} className="p-1.5 hover:bg-gray-700 rounded text-gray-400"><SafeIcon icon={FiList} /></button>
                </div>
              )}
              {warningCloudLimit && (
                <div className={`mb-3 flex gap-2 rounded-xl border px-3 py-2 text-xs leading-relaxed ${overCloudLimit ? 'border-red-500/30 bg-red-500/10 text-red-300' : nearCloudLimit ? 'border-orange-500/30 bg-orange-500/10 text-orange-300' : 'border-amber-500/30 bg-amber-500/10 text-amber-300'}`}>
                  <SafeIcon icon={FiAlertTriangle} className="mt-0.5 shrink-0" />
                  <div>
                    {overCloudLimit ? (
                      <>
                        This snippet is too large to sync safely to Google Sheets. Reduce it by <strong>{(cloudCharCount - CLOUD_SYNC_SAFE_LIMIT).toLocaleString()}</strong> characters before saving. Google Sheets has a hard {GOOGLE_SHEETS_CELL_LIMIT.toLocaleString()}-character limit per cell.
                      </>
                    ) : (
                      <>
                        Approaching the Google Sheets cell limit. {charsRemaining.toLocaleString()} cloud-safe characters remain.
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="relative">
                {type === 'plain' ? (
                  <textarea rows={8} className="w-full bg-gray-950 border border-gray-700 rounded-2xl px-4 py-3 text-white font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none resize-none min-h-[180px]" value={content} onChange={(e) => setContent(e.target.value)} required />
                ) : (
                  <div ref={editorRef} contentEditable onInput={handleInput} className="w-full bg-gray-950 border border-gray-700 rounded-2xl px-4 py-3 text-white text-sm focus:ring-2 focus:ring-indigo-500 outline-none min-h-[180px] rich-editor" />
                )}
              </div>
            </div>
            <div>
              <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-3 ml-1">Format Type</label>
              <div className="flex gap-4">
                {['plain', 'html'].map(t => (
                  <button key={t} type="button" onClick={() => {
                    if (t === type) return;
                    setContent(t === 'html' ? escapeToHtml(content) : htmlToPlainText(content));
                    setType(t);
                  }} className={`flex-1 py-3 rounded-xl text-sm font-bold border transition-all ${type === t ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg shadow-indigo-500/20' : 'bg-gray-950 border-gray-700 text-gray-500 hover:border-gray-500'}`}>
                    {t.toUpperCase()} {t === 'html' ? 'Rich' : 'Text'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-gray-800 bg-gray-900 sticky bottom-0 z-10 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-3 text-gray-400 font-bold hover:text-white transition-colors">CANCEL</button>
          <button onClick={handleSubmit} disabled={overCloudLimit} title={overCloudLimit ? 'Reduce content below the Google Sheets cloud-safe limit before saving' : 'Save snippet'} className="flex-[2] bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-white py-3 rounded-xl font-black text-sm tracking-widest transition-all shadow-lg shadow-indigo-500/20 uppercase">
            {overCloudLimit ? 'TOO LARGE TO SYNC' : 'SAVE SNIPPET'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}